import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeftIcon, 
  PencilIcon, 
  TrashIcon, 
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  CakeIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { contactService } from '../../services/ContactService';
import { auth } from '../../firebase';
import ContactFormModal from './ContactFormModal';
import NoteCard from '../NoteCard';
import NoteDetail from '../NoteDetail';
import ContactRow from './ContactRow'; // Use ContactRow instead of ContactCard
import Avatar from '../Avatar';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { userService } from '../../services/UserService';
import { useRecordingContext } from '../../contexts/RecordingContext';

function ContactDetail() {
  const { t } = useTranslation();
  const { contactId } = useParams();
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  const { setContactContext, clearContactContext } = useRecordingContext();
  
  const [contact, setContact] = useState(null);
  const [notes, setNotes] = useState([]);
  const [rawNotes, setRawNotes] = useState([]); // Store raw notes separately
  const [people, setPeople] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeTab, setActiveTab] = useState('notes'); // 'notes' or 'people'
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(true);

  // Subscribe to userService for folders
  useEffect(() => {
    const updateFolders = (userData) => {
      if (userData && userData.folders) {
        setFolders(userData.folders);
      } else {
        setFolders([]);
      }
    };

    // Initial load
    updateFolders(userService.currentUser);

    // Subscribe to changes
    const unsubscribe = userService.subscribe(updateFolders);

    return unsubscribe;
  }, []);

  const getFolderName = useCallback((folderId) => {
    if (!folderId || folderId === 'inbox') return t('notes.inbox');
    if (folderId === 'archived') return t('notes.archive');
    
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.name : t('common.unknown');
  }, [folders, t]);

  useEffect(() => {
    if (!currentUser || !contactId) return;

    const unsubscribeContact = contactService.subscribeToContact(contactId, (data) => {
      if (!data) {
         // Contact deleted or not found
         toast.error(t('contacts.contactNotFound'));
         navigate('/contacts');
         return;
      }
      setContact(data);
      setLoading(false);
      
      // If organization, default tab logic could be handled here, but 'notes' is fine default
    });

    const unsubscribeNotes = contactService.subscribeToContactNotes(currentUser.uid, contactId, (data) => {
        // Store raw notes - enrichment happens in separate effect
        setRawNotes(data);
    });

    // If organization, subscribe to people
    let unsubscribePeople = () => {};
    // We can't know if it's org until we fetch contact, so we might need another effect or just check inside
    // But we can just setup the subscription in a separate effect that depends on contact.type
    
    return () => {
      unsubscribeContact();
      unsubscribeNotes();
      unsubscribePeople();
    };
  }, [currentUser, contactId, navigate, t]);

  // Separate effect to enrich notes with folder names when folders or rawNotes change
  useEffect(() => {
    const enrichedNotes = rawNotes.map(note => ({
      ...note,
      folderName: getFolderName(note.folderId)
    }));
    setNotes(enrichedNotes);
  }, [rawNotes, getFolderName]);

  useEffect(() => {
    if (!currentUser || !contact || contact.type !== 'organization') {
        setPeople([]);
        return;
    }

    const unsubscribePeople = contactService.subscribeToOrganizationPeople(currentUser.uid, contact.id, (data) => {
        setPeople(data);
    });
    return () => unsubscribePeople();
  }, [currentUser, contact]);

  // Establecer el contexto del contacto cuando se carga
  useEffect(() => {
    if (contact) {
      setContactContext({
        id: contact.id,
        name: contact.name,
        surname: contact.surname,
        type: contact.type
      });
    }

    // Limpiar el contexto cuando se desmonta el componente
    return () => {
      clearContactContext();
    };
    // Note: setContactContext and clearContactContext are memoized so they won't cause re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, contact?.name, contact?.surname, contact?.type]);


  const handleDelete = async () => {
    if (window.confirm(t('contacts.deleteContactConfirm', { name: contact.name }))) {
      try {
        await contactService.deleteContact(contact.id);
        toast.success(t('contacts.contactDeletedSuccessfully'));
        navigate('/contacts');
      } catch (error) {
        toast.error(t('contacts.failedToDeleteContact'));
      }
    }
  };

  const handleNoteClick = (noteId) => {
      // We need the full note object. Find it in notes array
      const note = notes.find(n => n.id === noteId);
      if (note) setSelectedNote(note);
  };

  // Re-use logic from Notes.js for toggleCompletion if needed, 
  // but for now we can pass a dummy or minimal implementation 
  // since NoteCard requires it.
  const toggleTodoCompletion = async (noteId, currentStatus) => {
      // Implement if needed, or import from a common helper
      // For now, simple log or toast
      console.log('Toggle completion from ContactDetail not fully implemented');
  };

  if (loading) {
    return <div className="p-4 text-center text-gray-500">{t('contacts.loadingContacts')}</div>;
  }

  if (!contact) return null;

  const isPerson = contact.type === 'person';

  return (
    <div className="p-1 md:px-4 md:pb-4 md:pt-0 h-full flex flex-col overflow-y-auto">
       {/* Header */}
       <div className="flex items-center justify-between mb-6 pt-4 md:pt-0">
        <div className="flex items-center gap-x-3">
          <button onClick={() => navigate('/contacts')} className="text-gray-500 hover:text-gray-700">
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 truncate max-w-md">{contact.name} {isPerson && contact.surname}</h1>
        </div>
        <div className="flex gap-2">
            <button
                onClick={() => setIsEditModalOpen(true)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                title={t('common.edit')}
            >
                <PencilIcon className="h-5 w-5" />
            </button>
            <button
                onClick={handleDelete}
                className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                title={t('common.delete')}
            >
                <TrashIcon className="h-5 w-5" />
            </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
                <Avatar 
                    name={contact.name} 
                    surname={contact.surname} 
                    imageURL={contact.imageURL} 
                    type={contact.type}
                    size="xl"
                />
            </div>

            {/* Details Grid */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                 {/* Job & Org */}
                 {isPerson && (contact.jobTitle || contact.relatedAccountName) && (
                    <div className="col-span-2 flex items-center text-gray-700">
                        <BriefcaseIcon className="h-5 w-5 mr-2 text-gray-400" />
                        <span className="font-medium">{contact.jobTitle}</span>
                 {isPerson && contact.surname && <span className="mx-1">{t('contacts.at')}</span>}
                        {contact.relatedAccountName && (
                             <span 
                                className="text-orange-600 cursor-pointer hover:underline"
                                onClick={() => contact.relatedAccountId && navigate(`/contacts/${contact.relatedAccountId}`)}
                             >
                                {contact.relatedAccountName}
                             </span>
                        )}
                    </div>
                 )}

                 {/* Emails */}
                 {contact.emailAddresses?.map((email, i) => (
                     <div key={i} className="flex items-center text-gray-700">
                         <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-400" />
                         <a href={`mailto:${email}`} className="hover:text-orange-600">{email}</a>
                     </div>
                 ))}

                 {/* Phones */}
                 {contact.phoneNumbers?.map((phone, i) => (
                     <div key={i} className="flex items-center text-gray-700">
                         <PhoneIcon className="h-5 w-5 mr-2 text-gray-400" />
                         <a href={`tel:${phone}`} className="hover:text-orange-600">{phone}</a>
                     </div>
                 ))}

                 {/* Website */}
                 {contact.webPage && (
                     <div className="flex items-center text-gray-700">
                         <GlobeAltIcon className="h-5 w-5 mr-2 text-gray-400" />
                         <a href={contact.webPage} target="_blank" rel="noopener noreferrer" className="hover:text-orange-600 truncate max-w-xs">
                            {contact.webPage}
                         </a>
                     </div>
                 )}

                 {/* Birthday */}
                 {contact.birthday && (
                     <div className="flex items-center text-gray-700">
                         <CakeIcon className="h-5 w-5 mr-2 text-gray-400" />
                         <span>{format(contact.birthday.toDate(), 'MMMM d, yyyy')}</span>
                     </div>
                 )}
            </div>
        </div>
        
        {/* Notes Field */}
        {contact.notes && (
            <div className="mt-6 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-500 mb-2">{t('contacts.notes')}</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
            </div>
        )}
      </div>

      {/* Tabs for Organizations */}
      {!isPerson && (
        <div className="flex border-b border-gray-200 mb-4">
           <button
             className={`py-2 px-4 font-medium text-sm border-b-2 ${
               activeTab === 'notes' 
                 ? 'border-orange-500 text-orange-600' 
                 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
             }`}
             onClick={() => setActiveTab('notes')}
           >
             {t('notes.detail.noNoteAvailable')} ({notes.length})
           </button>
           <button
             className={`py-2 px-4 font-medium text-sm border-b-2 ${
               activeTab === 'people' 
                 ? 'border-orange-500 text-orange-600' 
                 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
             }`}
             onClick={() => setActiveTab('people')}
           >
             {t('contacts.peopleCount', { count: people.length })}
           </button>
        </div>
      )}

      {/* Content Section */}
      <div className="pb-20">
          {(!isPerson && activeTab === 'people') ? (
              /* People List */
              <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 shadow-sm overflow-hidden">
                  {people.map(person => (
                      <ContactRow
                          key={person.id}
                          contact={person}
                          onClick={(c) => navigate(`/contacts/${c.id}`)}
                      />
                  ))}
                  {people.length === 0 && (
                      <p className="text-gray-500 italic p-4">{t('contacts.noPeopleAssociated')}</p>
                  )}
              </div>
          ) : (
              /* Notes List */
              <div className="space-y-4">
                  {isPerson && <h3 className="text-lg font-medium text-gray-900 mb-4">{t('contacts.relatedNotes')} ({notes.length})</h3>}
                  
                  {notes.map(note => (
                      <div key={note.id} onClick={() => handleNoteClick(note.id)} className="cursor-pointer">
                          <NoteCard 
                             note={note} 
                             toggleCompletion={toggleTodoCompletion} 
                          />
                      </div>
                  ))}
                  {notes.length === 0 && (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                          <p className="text-gray-500">{t('contacts.noRelatedNotesFound')}</p>
                          <p className="text-sm text-gray-400 mt-1">{t('contacts.createNoteAddContact')}</p>
                      </div>
                  )}
              </div>
          )}
      </div>

      {/* Edit Modal */}
      {currentUser && (
        <ContactFormModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          contactToEdit={contact}
          uid={currentUser.uid}
        />
      )}

      {/* Note Detail Modal */}
      {selectedNote && (
        <NoteDetail
          note={selectedNote}
          onClose={() => setSelectedNote(null)}
          onDelete={(id) => {
             setSelectedNote(null);
             // Notes list updates automatically via subscription
          }}
        />
      )}

    </div>
  );
}

export default ContactDetail;
